/**
 * Kwalificatie Matrix Page (Epic C-2 — Kwalificaties, Story C-2.2, UX-C3)
 *
 * Tab *Kwalificaties* onder "Planning" (nav-integratie volgt in Story
 * C-5.2, zelfde incrementele opleverpatroon als `/planning/taken`,
 * C-1.3). Segmented control "Per medewerker" (default) / "Wie kan wat" +
 * de HR-only "Lijsten beheren"-ingang uit Story C-2.1.
 *
 * De medewerkerslijst en het afdelingsfilter staan hier, niet in de twee
 * weergaven. Dat is geen opruimwerk maar een reparatie: de tabbladen renderen
 * conditioneel, dus elke wissel unmount de actieve weergave. Stond de
 * filterkeuze daarin, dan zette de standaard zichzelf bij terugkomst opnieuw en
 * verloor de gebruiker zijn keuze — en hielden de twee tabbladen bovendien elk
 * hun eigen afdeling bij.
 */
import { useMemo, useState } from 'react';
import { Layout } from '../../components/Layout';
import { LijstenBeherenMenu } from './components/LijstenBeherenMenu';
import { PerMedewerkerMatrix } from './components/PerMedewerkerMatrix';
import { SegmentedControl, type SegmentedControlOption } from './components/SegmentedControl';
import { WieKanWatMatrix } from './components/WieKanWatMatrix';
import { useScopedEmployees } from './hooks/useQualifications';
import { useStandaardAfdeling } from './hooks/useStandaardAfdeling';

type MatrixView = 'per-medewerker' | 'wie-kan-wat';

const VIEW_OPTIONS: readonly SegmentedControlOption<MatrixView>[] = [
  { value: 'per-medewerker', label: 'Per medewerker' },
  { value: 'wie-kan-wat', label: 'Wie kan wat' },
];

export function KwalificatieMatrixPage() {
  const [view, setView] = useState<MatrixView>('per-medewerker');

  // Zonder filters opgehaald — één keer, voor beide weergaven. Zou de server
  // op afdeling of zoekterm filteren, dan zouden de afdelingsopties (die uit
  // déze lijst komen) meekrimpen: kies je een afdeling of typ je een naam, dan
  // blijft er één optie over en kun je niet meer terug.
  const { data: employees, isLoading: employeesLoading } = useScopedEmployees({});

  const departmentOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const emp of employees ?? []) {
      if (emp.departmentId !== null && emp.departmentName && !seen.has(emp.departmentId)) {
        seen.set(emp.departmentId, emp.departmentName);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, 'nl')
    );
  }, [employees]);

  const { departmentId, setDepartmentId } = useStandaardAfdeling(departmentOptions);

  const zichtbareMedewerkers = (employees ?? []).filter(
    (emp) => departmentId === null || emp.departmentId === departmentId
  );

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-[#1E293B]">Kwalificaties</h1>
          <LijstenBeherenMenu />
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <SegmentedControl options={VIEW_OPTIONS} value={view} onChange={setView} ariaLabel="Weergave" />
          <select
            data-testid="kwalificatie-afdeling-filter"
            aria-label="Afdeling"
            value={departmentId ?? ''}
            onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          >
            <option value="">Alle afdelingen</option>
            {departmentOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        {view === 'per-medewerker' ? (
          <PerMedewerkerMatrix employees={zichtbareMedewerkers} isLoading={employeesLoading} />
        ) : (
          <WieKanWatMatrix employees={zichtbareMedewerkers} isLoading={employeesLoading} />
        )}
      </div>
    </Layout>
  );
}
